import '../../styles/crud-editor.scss';
import { FieldSchema, CrudStatus, CrudStep, EntryStatus } from '../types';
import { Form, Table, Button, InputTextField, RawFieldRenderer } from 'forms';
import {Dictionary} from 'lodash';
import classNames from 'classnames';
import {CollectionField} from '../fields';
import CrudReview from './CrudReview';
import {
    CrudCreateEditor,
    CrudFooter,
    OrderByHeadersRowCells,
    StatusOverlay,
} from '../controls';
import React from 'react';
import t from 'rcm-i18n';
import {TableCellRenderer} from '../renderers';
import CrudProgressReporter from './CrudProgressReporter';

interface Props<V extends object> {
    className: string;
    creates: Dictionary<V>;
    deletes: Dictionary<V>;
    disabled: boolean;
    filter: string;
    filterDebounce: number;
    filterPlaceholder: string;
    getValue: (id: string) => V;
    labels: string;
    onCreateChange: (v: V, id: string) => void;
    onCreateNew: () => void;
    onCreateRemove: (id: string) => void;
    onDiscardAll: () => void;
    onFilterChange: (filter: string) => void;
    onOrderChange: (order: {by: keyof V, descending: boolean}) => void;
    onReview: () => void;
    onReviewCancel: () => void;
    onReviewSubmit: () => void;
    onSaveClose: () => void;
    onSaveStop: () => void;
    onValueChange: (value: V) => void;
    onValueRemove: (value: V) => void;
    onValueReset: (value: V) => void;
    order: { by?: keyof V, descending?: boolean };
    schema: FieldSchema[];
    schemaVisible: FieldSchema[];
    status: CrudStatus;
    statuses: Array<EntryStatus<V>>;
    step: CrudStep;
    totalEdits: number;
    updates: Dictionary<V>;
    values: Array<{ checked: boolean, edited: boolean, id: string, value: V }>;
}

const CrudEditor = <V extends object>({
    className = '',
    creates,
    deletes,
    disabled,
    filter,
    filterDebounce,
    filterPlaceholder,
    getValue,
    labels,
    onCreateChange,
    onCreateNew,
    onCreateRemove,
    onDiscardAll,
    onFilterChange,
    onOrderChange,
    onReview,
    onReviewCancel,
    onReviewSubmit,
    onSaveClose,
    onSaveStop,
    onValueChange,
    onValueRemove,
    onValueReset,
    order,
    schema,
    schemaVisible,
    status,
    statuses,
    step,
    totalEdits,
    updates,
    values,
}: Props<V>) => <Form className={classNames(
    'crud-editor',
    className,
    `crud-editor-step-${step}`
)}>
    {step === 'edit' ? <>
        <Table className="crud-editor-table"
                cols={schemaVisible.length + 2}>
            <tr>
                <th>{t('Search')}</th>
                <td colSpan={schemaVisible.length}>
                    <InputTextField
                        Renderer={RawFieldRenderer}
                        debounce={filterDebounce}
                        onValueChange={onFilterChange}
                        value={filter}
                        placeholder={filterPlaceholder || labels}
                        disabled={disabled}
                    />
                </td>
            </tr>
            {values.length > 1 && <tr>
                <th>{t('Sort')}</th>
                <OrderByHeadersRowCells
                    disabled={disabled}
                    order={order}
                    schema={schema}
                    onOrderChange={onOrderChange}
                />
            </tr>}
            {(values.length === 0 && filter) && <tr>
                <td />
                <td className="no-results" colSpan={schemaVisible.length}>
                    {t('No results')}
                </td>
            </tr>}
            {values.map(({id, value, edited, checked}, i) =>
                <tr key={id}>
                    {i === 0 ? <th>{t('Results')}</th> : <td /> }
                    <CollectionField
                        Renderer={RawFieldRenderer}
                        SubRenderer={TableCellRenderer}
                        disabled={disabled || checked}
                        schema={schema}
                        value={value}
                        onValueChange={onValueChange}
                    />
                    <td>
                        {edited ? <Button
                            disabled={disabled || checked}
                            text={t('Revert')}
                            btn="default"
                            icon="refresh"
                            onClick={() => onValueReset(value)}
                        /> : <Button btn="danger"
                            disabled={disabled}
                            text={t('Remove')}
                            icon={checked ? 'check' : 'unchecked'}
                            onClick={() => onValueRemove(value)}
                        />}
                    </td>
                </tr>
            )}
        </Table>
        <CrudCreateEditor
            creates={creates}
            disabled={disabled}
            schema={schema}
            onUpdate={onCreateChange}
            onDiscard={onCreateRemove}
        />
        <CrudFooter
            disabled={disabled}
            onNew={onCreateNew}
            onRevertAll={onDiscardAll}
            onReview={onReview}
            totalEdits={totalEdits}
        />
    </> : (step === 'review') ? <CrudReview
        creates={creates}
        deletes={deletes}
        getValue={getValue}
        onCancel={onReviewCancel}
        onSubmit={onReviewSubmit}
        schema={schema}
        updates={updates}
    /> : (step === 'save') ? <CrudProgressReporter
        onClose={onSaveClose}
        schema={schema}
        status={status}
        statuses={statuses}
        onStop={onSaveStop}
    /> : null}
    <StatusOverlay status={status} />
</Form>;

export default CrudEditor;
